import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatTableText } from '@/lib/tableUtils';

const TripReportTable = ({ reports = [] }) => {
  const formatVal = (val) => {
    if (val === null || val === undefined || val === 'N/A' || val === 'None' || val === 'null' || val === '') {
      return '--';
    }
    const formatted = formatTableText(val);
    return formatted === 'N/A' ? '--' : formatted;
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'COMPLETED':
      case 'AUTO_COMPLETE':
      case 'AUTO_COMPLETED':
        return 'completed';
      case 'ACTIVE':
        return 'active';
      default:
        return 'delayed';
    }
  };

  const formatStatus = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'COMPLETED':
        return 'Completed';
      case 'AUTO_COMPLETE':
      case 'AUTO_COMPLETED':
        return 'Auto Completed';
      case 'DELAYED':
        return 'Delayed';
      case 'SCHEDULED':
        return 'Scheduled';
      case 'PENDING':
        return 'Pending';
      default:
        return status;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString || dateString === 'N/A' || dateString === 'None' || dateString === 'null') {
      return '--';
    }

    try {
      let date;

      if (/^\d{2}\/\d{2}\/\d{4}\s\d{2}:\d{2}$/.test(dateString)) {
        const [datePart, timePart] = dateString.split(' ');
        const [day, month, year] = datePart.split('/');
        const [hours, minutes] = timePart.split(':');
        date = new Date(year, month - 1, day, hours, minutes);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        return '--';
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return '--';
    }
  };

  const hasValue = (key) => {
    return reports.some((report) => {
      const val = report[key];
      return val !== null && val !== undefined && val !== 'N/A' && val !== 'None' && val !== 'null' && val !== '';
    });
  };

  if (!reports || reports.length === 0) {
    return null;
  }

  return (
    <div className="rounded-md border overflow-hidden w-full">
      <div className="max-h-[calc(100vh-250px)] overflow-auto w-full">
        <Table>
          <TableHeader className="sticky top-0 z-10">
            <TableRow>
              <TableHead className="h-10 px-4 py-1 min-w-[150px]">Trip Number</TableHead>
              <TableHead className="h-10 px-4 py-1 min-w-[120px]">Vehicle Number</TableHead>
              <TableHead className="h-10 px-4 py-1 min-w-[150px]">Product</TableHead>
              <TableHead className="h-10 px-4 py-1 min-w-[100px]">Quantity</TableHead>
              <TableHead className="h-10 px-4 py-1 min-w-[100px]">Status</TableHead>
              {hasValue('consignee_name') && (
                <TableHead className="h-10 px-4 py-1 min-w-[150px]">Consignee</TableHead>
              )}
              <TableHead className="h-10 px-4 py-1 min-w-[150px]">Driver</TableHead>
              <TableHead className="h-10 px-4 py-1 min-w-[150px]">Start Time</TableHead>
              <TableHead className="h-10 px-4 py-1 min-w-[150px]">End Time</TableHead>
              {hasValue('port_name') && (
                <TableHead className="h-10 px-4 py-1 min-w-[150px]">Port</TableHead>
              )}
              {hasValue('distributor_name') && (
                <TableHead className="h-10 px-4 py-1 min-w-[150px]">Distributor</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((report, index) => (
              <TableRow key={index} className="hover:bg-transparent border-border">
                <TableCell className="h-10 px-4 py-1 font-medium">{formatVal(report.trip_number)}</TableCell>
                <TableCell className="h-10 px-4 py-1 uppercase">{formatVal(report.vehicle_number)}</TableCell>
                <TableCell className="h-10 px-4 py-1">{formatVal(report.product_type_name)}</TableCell>
                <TableCell className="h-10 px-4 py-1">{formatVal(report.product_quantity)}</TableCell>
                <TableCell className="h-10 px-4 py-1">
                  <Badge
                    variant={getStatusVariant(report.status)}
                    className="inline-block text-center w-[80px]"
                  >
                    {formatVal(formatStatus(report.status))}
                  </Badge>
                </TableCell>
                {hasValue('consignee_name') && (
                  <TableCell className="h-10 px-4 py-1">{formatVal(report.consignee_name)}</TableCell>
                )}
                <TableCell className="h-10 px-4 py-1">{formatVal(report.username)}</TableCell>
                <TableCell className="h-10 px-4 py-1">{formatDateTime(report.actual_start_time)}</TableCell>
                <TableCell className="h-10 px-4 py-1">{formatDateTime(report.actual_end_time)}</TableCell>
                {hasValue('port_name') && (
                  <TableCell className="h-10 px-4 py-1">{formatVal(report.port_name)}</TableCell>
                )}
                {hasValue('distributor_name') && (
                  <TableCell className="h-10 px-4 py-1">{formatVal(report.distributor_name)}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default TripReportTable;
